import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  green: '#4CAF82',
  red: '#E05C5C',
  text: '#F0EDE8',
  muted: '#888',
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
  input { color-scheme: dark; font-family: 'Inter', sans-serif; }
`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [failedHard, setFailedHard] = useState(''); // expired/invalid link

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 404 or 410 = link unusable
        if (res.status === 404 || res.status === 410) {
          setFailedHard(data.detail || 'This reset link is no longer valid.');
        } else {
          throw new Error(data.detail || 'Something went wrong.');
        }
      } else {
        setDone(true);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const inp = {
    width: '100%',
    padding: '12px 14px',
    background: C.surf,
    border: `0.5px solid ${C.bdr}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color .15s',
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 400, animation: 'fadeUp .4s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 36, justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>C</div>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.5px' }}>
              Content<span style={{ color: C.accent }}>Gap</span>
            </span>
          </div>

          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 12, padding: '28px 26px' }}>
            {done ? (
              <div style={{ textAlign: 'center' }}>
                <CheckCircle2 size={42} color={C.green} strokeWidth={1.6} style={{ marginBottom: 14 }} />
                <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 8, letterSpacing: '-.3px' }} data-testid="reset-success-heading">Password reset!</h1>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.6, marginBottom: 22 }}>
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  data-testid="go-to-signin-btn"
                  style={{ padding: '11px 24px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
                >
                  Go to sign in →
                </button>
              </div>
            ) : failedHard ? (
              <div style={{ textAlign: 'center' }}>
                <XCircle size={42} color={C.red} strokeWidth={1.6} style={{ marginBottom: 14 }} />
                <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 8, letterSpacing: '-.3px' }}>Link invalid</h1>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.6, marginBottom: 22 }}>{failedHard}</p>
                <Link
                  to="/forgot-password"
                  style={{ display: 'inline-block', padding: '11px 24px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
                >
                  Request a new link
                </Link>
              </div>
            ) : (
              <>
                <h1 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-.5px', marginBottom: 6 }} data-testid="reset-heading">Set a new password</h1>
                <p style={{ fontSize: 15, color: C.muted, marginBottom: 22, lineHeight: 1.6 }}>
                  Choose a strong password (at least 6 characters).
                </p>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>New password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 6 characters"
                        style={{ ...inp, paddingRight: 38 }}
                        data-testid="reset-password-input"
                        autoFocus
                        onFocus={(e) => (e.target.style.borderColor = C.accent)}
                        onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', padding: 5, display: 'flex', alignItems: 'center' }}
                      >
                        {showPassword ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>Confirm new password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-type your new password"
                      style={inp}
                      data-testid="reset-confirm-input"
                      onFocus={(e) => (e.target.style.borderColor = C.accent)}
                      onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                    />
                  </div>

                  {error && (
                    <div data-testid="reset-error" style={{ background: 'rgba(220,60,60,0.10)', border: '0.5px solid rgba(220,60,60,0.3)', borderRadius: 7, padding: '9px 13px', fontSize: 13, color: '#FF7070', marginBottom: 14 }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: C.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    {loading ? 'Resetting...' : 'Reset password'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 14, color: C.muted }}>
            <Link to="/login" style={{ color: C.accent, fontWeight: 500, textDecoration: 'none' }}>
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
